import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { REGISTER_URL, SIGNIN_URL } from "@/data/site";
import { OFundedLogo } from "@/components/BrandLogo";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { prefetch, prefetchProps } from "@/lib/prefetch";
import { MegaRibbon, MobileChallengePicker, PLATFORM_MAP } from "@/components/Navbar.MegaMenu";
import { useTranslatedMegaMenu } from "@/data/megaMenuI18n";
import { pushOverlay, popOverlay } from "@/lib/overlayState";

export default function Navbar() {
  const { t } = useTranslation();
  const MEGA_MENU_T = useTranslatedMegaMenu();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();
  const closeTimer = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setActiveKey(null);
    setMobileExpanded(null);
  }, [location.pathname]);

  // Close mega-menu on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActiveKey(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Click-outside to close the mega-menu — solves the "menu kept
  // disappearing when I tried to move my cursor" frustration. We
  // attach a window pointerdown listener while the menu is open and
  // close it as soon as the user clicks outside the header.
  useEffect(() => {
    if (!activeKey) return;
    const onDocDown = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setActiveKey(null);
      }
    };
    document.addEventListener("pointerdown", onDocDown);
    return () => document.removeEventListener("pointerdown", onDocDown);
  }, [activeKey]);

  const scheduleClose = () => {
    // No-op now — we only close on explicit click-outside or ESC.
    // (Previously this fired a 220ms timeout on mouseleave which made
    // the menu feel like it had a mind of its own.)
  };
  const cancelClose = () => {};
  const openSection = (key) => {
    setActiveKey(key);
    // Eagerly warm chunks for every link in this section so the
    // upcoming click feels instant.
    const entry = MEGA_MENU_T.find((e) => e.key === key);
    if (entry) {
      if (entry.picker === "challenges") {
        for (const p of entry.programs || []) {
          for (const s of p.sizes) prefetch(s.to.split("?")[0]);
        }
      } else {
        for (const col of entry.columns) {
          for (const it of col.items) prefetch(it.to.split("?")[0]);
        }
      }
      if (entry.spotlight?.cta?.to) prefetch(entry.spotlight.cta.to.split("?")[0]);
    }
  };

  // Lock body scroll + hide Tawk while the full-screen mobile overlay
  // is open. Tawk's launcher iframe has a z-index of ~1e6 so it would
  // otherwise sit on top of the menu.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    pushOverlay();
    return () => {
      document.body.style.overflow = prev;
      popOverlay();
    };
  }, [open]);

  const activeEntry = activeKey ? MEGA_MENU_T.find((e) => e.key === activeKey) : null;

  return (
    <header
      ref={headerRef}
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-[60] transition-all ${
        scrolled || activeEntry
          ? "backdrop-blur-xl bg-[color:var(--surface-3)] border-b border-[color:var(--line)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 lg:px-8 h-16 relative">
        <Link
          to="/"
          data-testid="logo-home-link"
          className="flex items-center group shrink-0"
        >
          <OFundedLogo className="h-5 lg:h-6 w-auto" />
        </Link>

        {/* Desktop trigger row — CLICK to open. Hover does nothing. */}
        <nav className="hidden lg:flex items-center gap-1">
          {MEGA_MENU_T.map((entry) => {
            const isActive = activeKey === entry.key;
            return (
              <button
                key={entry.key}
                type="button"
                data-testid={`nav-link-${entry.key}`}
                onClick={() => (isActive ? setActiveKey(null) : openSection(entry.key))}
                aria-expanded={isActive}
                className={`h-9 inline-flex items-center gap-1 px-4 text-sm rounded-lg transition-colors ${
                  isActive
                    ? "text-[color:var(--text-1)] bg-[color:var(--surface-3)]"
                    : "text-[color:var(--text-2)] hover:text-[color:var(--text-1)]"
                }`}
              >
                {entry.label}
                <ChevronDown
                  size={13}
                  className={`transition-transform ${isActive ? "rotate-180" : ""}`}
                />
              </button>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <LanguageSwitcher variant="desktop" />
          <ThemeToggle />
          <a
            href={SIGNIN_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="signin-button"
            className="h-9 w-[104px] inline-flex items-center justify-center text-sm font-medium rounded-[10px] border border-[color:var(--line-strong)] text-[color:var(--text-1)] hover:border-brand/60 hover:bg-[color:var(--surface-2)] transition-colors"
          >
            {t("nav.signIn")}
          </a>
          <a
            href={REGISTER_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="register-button"
            className="h-9 w-[104px] inline-flex items-center justify-center text-sm font-medium rounded-[10px] text-[#071116] transition-all"
            style={{
              background: "linear-gradient(135deg, var(--brand) 0%, var(--brand-deep) 100%)",
              boxShadow: "0 1px 3px color-mix(in srgb, var(--brand) 6%, transparent), inset 0 1px 0 rgba(255,255,255,0.30)",
            }}
          >
            {t("nav.register")}
          </a>
        </div>

        <div className="flex lg:hidden items-center gap-2">
          <LanguageSwitcher variant="mobile" />
          <ThemeToggle />
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label={t("nav.toggle_menu", "Toggle menu")}
            data-testid="mobile-menu-toggle"
            className="text-[color:var(--text-1)] p-2"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Desktop full-width mega-menu ribbon — opens on CLICK, stays
          open until the user clicks outside the header or presses ESC. */}
      <div className="hidden lg:block">
        <AnimatePresence mode="wait">
          {activeEntry && <MegaRibbon entry={activeEntry} onClose={() => setActiveKey(null)} />}
        </AnimatePresence>
      </div>

      {/* Mobile menu — full-screen overlay rendered as a single
          scrollable surface. Every category is visible (no tab
          switching), and every link is an icon-tile so the user can
          scan the whole site at a glance. Quick-jump pills at the top
          smooth-scroll to a category inside the overlay. */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-fs"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="lg:hidden fixed top-0 left-0 right-0 bottom-0 w-screen h-screen z-[70] flex flex-col"
            data-testid="mobile-menu"
            style={{ background: "var(--surface)", height: "100dvh", width: "100vw" }}
          >
            {/* Header bar inside the overlay — logo + close */}
            <div className="flex items-center justify-between px-5 h-16 border-b border-[color:var(--line)]/60 shrink-0">
              <Link to="/" onClick={() => setOpen(false)} className="flex items-center">
                <OFundedLogo className="h-5 w-auto" />
              </Link>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t("nav.close_menu", "Close menu")}
                data-testid="mobile-menu-close"
                className="w-10 h-10 rounded-full grid place-items-center text-[color:var(--text-1)] hover:bg-[color:var(--surface-2)] transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body — every category + every link laid out as an icon
                grid. 3-column grid of square tiles so visitors can scan
                the entire site in two thumb-scrolls. Generous bottom
                padding so the last category isn't hidden behind the
                sticky CTA bar at the bottom. */}
            <div
              className="flex-1 overflow-y-auto px-3 pt-3 pb-32"
              data-testid="mobile-menu-scroll"
              style={{ overscrollBehavior: "contain" }}
            >
              {MEGA_MENU_T.map((entry) => (
                <section
                  key={entry.key}
                  id={`m-cat-${entry.key}`}
                  data-testid={`mobile-cat-${entry.key}`}
                  className="mt-5 first:mt-3"
                >
                  {/* Category title */}
                  <div className="px-1 mb-2.5">
                    <h3 className="text-[15px] font-semibold text-[color:var(--text-1)] tracking-tight">
                      {entry.label}
                    </h3>
                  </div>

                  {entry.picker === "challenges" ? (
                    /* Mobile two-step picker: program type pills at top,
                       then sizes for the chosen program below. State is
                       local to this mobile section so it doesn't affect
                       the desktop ribbon picker. */
                    <MobileChallengePicker entry={entry} onClose={() => setOpen(false)} />
                  ) : entry.columns.map((col) => (
                    <div key={col.heading} className="mb-3 last:mb-0">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-3)] font-semibold mb-2 px-1">
                        {col.heading}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {col.items.map((it) => {
                          const plat = it.platformId ? PLATFORM_MAP[it.platformId] : null;
                          return (
                            <Link
                              key={it.label}
                              to={it.to}
                              onClick={() => setOpen(false)}
                              data-testid={`mobile-link-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              {...prefetchProps(it.to.split("?")[0])}
                              className="group relative flex flex-col items-center justify-start gap-2 p-2.5 rounded-xl min-h-[92px] text-center active:scale-[0.97] transition-transform"
                              style={plat ? {
                                background: `color-mix(in srgb, ${plat.accent} 7%, var(--surface-2))`,
                                border: `1px solid color-mix(in srgb, ${plat.accent} 24%, var(--line))`,
                              } : {
                                background: "color-mix(in srgb, var(--brand) 5%, var(--surface-2))",
                                border: "1px solid color-mix(in srgb, var(--brand) 14%, var(--line))",
                              }}
                            >
                              <span
                                className="w-10 h-10 rounded-lg grid place-items-center shrink-0 overflow-hidden"
                                style={plat ? {
                                  background: `color-mix(in srgb, ${plat.accent} 16%, rgba(0,0,0,0.4))`,
                                  border: `1px solid color-mix(in srgb, ${plat.accent} 50%, transparent)`,
                                } : {
                                  background: "color-mix(in srgb, var(--brand) 14%, transparent)",
                                  border: "1px solid color-mix(in srgb, var(--brand) 32%, transparent)",
                                  boxShadow: "inset 0 1px 0 color-mix(in srgb, var(--brand) 18%, transparent)",
                                }}
                              >
                                {plat ? (
                                  <img src={plat.logo} alt={plat.name} className="w-full h-full object-contain p-1" loading="lazy" />
                                ) : (
                                  <it.Icon size={17} className="text-brand" strokeWidth={2.2} />
                                )}
                              </span>
                              <span className="block text-[11.5px] font-medium text-[color:var(--text-1)] leading-tight line-clamp-2 px-0.5">
                                {it.label}
                              </span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Per-category spotlight — flashy CTA tile at the
                      bottom of the section. Keeps the "product-y" feel
                      from the desktop ribbon. */}
                  {entry.spotlight && (
                    <Link
                      to={entry.spotlight.cta.to}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-spotlight-${entry.key}`}
                      className="mt-2 flex items-center gap-3 p-3 rounded-xl"
                      style={{
                        background:
                          "linear-gradient(135deg, color-mix(in srgb, var(--brand) 18%, var(--surface-2)) 0%, var(--surface-2) 70%)",
                        border: "1px solid color-mix(in srgb, var(--brand) 32%, transparent)",
                      }}
                    >
                      <span
                        className="w-9 h-9 rounded-lg grid place-items-center shrink-0"
                        style={{
                          background: "color-mix(in srgb, var(--brand) 22%, transparent)",
                          border: "1px solid color-mix(in srgb, var(--brand) 40%, transparent)",
                        }}
                      >
                        {entry.spotlight.icon ? (
                          <entry.spotlight.icon size={15} className="text-brand" />
                        ) : (
                          <Sparkles size={15} className="text-brand" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1 text-left">
                        <span className="block text-[10px] uppercase tracking-[0.22em] text-brand font-semibold">
                          {entry.spotlight.eyebrow}
                        </span>
                        <span className="block text-[13px] font-medium text-[color:var(--text-1)] leading-tight mt-0.5 truncate">
                          {entry.spotlight.title}
                        </span>
                      </span>
                      <ArrowUpRight size={16} className="text-brand shrink-0" />
                    </Link>
                  )}
                </section>
              ))}
            </div>

            {/* Sticky CTA pair at the bottom — primary action always
                reachable with a thumb. */}
            <div
              className="p-3 border-t border-[color:var(--line)]/60 flex items-center gap-3 shrink-0"
              style={{
                paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
                background: "var(--surface)",
              }}
            >
              <a
                href={SIGNIN_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta-signin"
                className="flex-1 h-12 inline-flex items-center justify-center text-[14px] font-medium rounded-xl border border-[color:var(--line-strong)] text-[color:var(--text-1)]"
              >
                {t("nav.signIn")}
              </a>
              <a
                href={REGISTER_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta-register"
                className="flex-1 h-12 inline-flex items-center justify-center text-[14px] font-semibold rounded-xl"
                style={{
                  background: "linear-gradient(135deg, var(--brand) 0%, var(--brand-deep) 100%)",
                  color: "#071116",
                  boxShadow: "0 4px 14px color-mix(in srgb, var(--brand) 30%, transparent)",
                }}
              >
                {t("nav.register")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
